import { useTranslation } from "react-i18next";
import { useApi } from "src/hooks/useApi";
import { DetailCard } from "src/components/Details/Details";
import {
    TbUserPlus,
    TbLogin,
    TbMessage,
    TbFileTypePdf,
    TbEye,
} from "react-icons/tb";

interface PortalActivity {
    id: string;
    type: "register" | "login" | "sms_otp_sent" | "pdf_downloaded" | "invoice_viewed";
    createdAt: string;
    meta?: Record<string, unknown>;
}

function MetaDetail({ type, meta }: { type: PortalActivity["type"]; meta?: Record<string, unknown> }) {
    if (!meta) return null;
    if (type === "register" && meta.email) return <span className="portal-activity-meta">{String(meta.email)}</span>;
    if (type === "sms_otp_sent" && meta.phoneMasked) return <span className="portal-activity-meta">{String(meta.phoneMasked)}</span>;
    if (type === "pdf_downloaded" && meta.disposition) return <span className="portal-activity-meta">{meta.disposition === "attachment" ? "download" : "view"}</span>;
    return null;
}

function fmtDate(iso: string) {
    return new Date(iso).toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

export function PortalActivityCard({ invoiceId }: { invoiceId: string }) {
    const { t } = useTranslation();
    const { data, loading } = useApi<{ activities: PortalActivity[] }>(
        `/invoices/${invoiceId}/portal-activity`,
    );

    const activities = data?.activities ?? [];

    const TYPE_CONFIG: Record<PortalActivity["type"], { icon: JSX.Element; label: string; color: string }> = {
        register:        { icon: <TbUserPlus />,   label: t("invoicePage.portalEventRegister"),      color: "#16a34a" },
        login:           { icon: <TbLogin />,       label: t("invoicePage.portalEventLogin"),          color: "#0277bd" },
        sms_otp_sent:    { icon: <TbMessage />,     label: t("invoicePage.portalEventSmsOtp"),         color: "#7c3aed" },
        pdf_downloaded:  { icon: <TbFileTypePdf />, label: t("invoicePage.portalEventPdfDownloaded"),  color: "#ea580c" },
        invoice_viewed:  { icon: <TbEye />,         label: t("invoicePage.portalEventInvoiceViewed"),  color: "#6b7280" },
    };

    return (
        <DetailCard title={t("invoicePage.portalTitle")} full>
            {loading ? (
                <p className="invoice-log__empty">{t("invoicePage.portalLoading")}</p>
            ) : activities.length === 0 ? (
                <p className="invoice-log__empty">{t("invoicePage.portalNoActivity")}</p>
            ) : (
                <table className="invoice-log">
                    <thead>
                        <tr>
                            <th>{t("invoicePage.portalColDate")}</th>
                            <th>{t("invoicePage.portalColEvent")}</th>
                            <th>{t("invoicePage.portalColDetails")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activities.map((a) => {
                            const cfg = TYPE_CONFIG[a.type] ?? { icon: <TbEye />, label: a.type, color: "#6b7280" };
                            return (
                                <tr key={a.id}>
                                    <td className="invoice-log__date">{fmtDate(a.createdAt)}</td>
                                    <td>
                                        <span className="portal-activity-event" style={{ color: cfg.color }}>
                                            {cfg.icon}
                                            {cfg.label}
                                        </span>
                                    </td>
                                    <td className="invoice-log__val">
                                        <MetaDetail type={a.type} meta={a.meta} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </DetailCard>
    );
}
